import { useEffect, useRef, useState } from "react";
import { animate, motion, useInView } from "framer-motion";
import { useSite } from "@/lib/useSite";
import { STAT_ICONS } from "@/lib/statIcons";

const DEFAULT_STATS = [
  { icon: "building", value: 1200, suffix: "+", label: "Projects Completed" },
  { icon: "users", value: 4800, suffix: "+", label: "Happy Customers" },
  { icon: "hardhat", value: 45, suffix: "+", label: "Fabricators" },
  { icon: "layers", value: 500000, suffix: "+", label: "Sq.Ft of Roofing" },
];

function Counter({ to, suffix }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration: 2.2,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setVal(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, to]);

  return (
    <span ref={ref}>
      {val.toLocaleString("en-IN")}
      <span className="text-srs">{suffix}</span>
    </span>
  );
}

export default function StatsCounter() {
  const { data } = useSite();
  const stats = data?.settings?.stats?.length ? data.settings.stats : DEFAULT_STATS;

  return (
    <section data-testid="stats-counter" className="mx-auto max-w-[1400px] px-6 py-20 sm:px-10 sm:py-24">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="text-center"
      >
        <h2 className="font-display text-3xl font-semibold tracking-tight text-ink sm:text-4xl">
          Our <span className="text-srs">Company</span>
        </h2>
        <svg className="mx-auto mt-3" width="58" height="10" viewBox="0 0 58 10" fill="none" aria-hidden="true">
          <path d="M2 6 Q8.5 1 15 6 T28 6 T41 6 T56 6" stroke="#D32F2F" strokeWidth="2.5" strokeLinecap="round" />
        </svg>
      </motion.div>

      <div className="mt-14 grid grid-cols-2 gap-y-12 lg:grid-cols-4">
        {stats.map((s, i) => {
          const Icon = STAT_ICONS[s.icon] || STAT_ICONS.building;
          return (
            <motion.div
              key={`${s.label}-${i}`}
              data-testid={`stat-item-${i}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
              className="flex flex-col items-center text-center"
            >
              <span className="flex h-16 w-16 items-center justify-center rounded-[1.4rem] bg-srs text-white shadow-[0_16px_35px_-14px_rgba(211,47,47,0.55)] transition-transform duration-300 hover:-translate-y-1">
                <Icon size={26} strokeWidth={1.8} />
              </span>
              <p className="mt-6 font-display text-3xl font-bold tracking-tight text-ink sm:text-4xl">
                <Counter to={Number(s.value) || 0} suffix={s.suffix || ""} />
              </p>
              <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-subtle">{s.label}</p>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}
